const COMPLETION_EVENT_TYPES = [
  'Lesson Completed',
  'Lesson Completion',
  'Lesson End',
  'Lesson Result'
]

export function resolvePassedSafe(data, scoreString) {
  if (data.passed === true || data.passed === 'true') return true
  if (data.passed === false || data.passed === 'false') return false

  const sd = data.section_data
  if (sd) {
    if (sd.passed === true || sd.passed === 'true') return true
    if (sd.passed === false || sd.passed === 'false') return false
  }

  if (scoreString) {
    const match = String(scoreString).match(/^(\d+)\/(\d+)$/)
    if (match && parseInt(match[1]) === parseInt(match[2])) return true
  }

  return null
}

export function parseScoreLegacy(log) {
  let pointsEarned = ''
  let totalPoints = ''
  let scorePercentage = ''

  const context = log.context || {}
  const sectionData = context.section_data || {}
  const eventType = log.event_type || ''

  const parseFraction = (raw) => {
    const cleaned = String(raw).replace(/\s*\(.*\)/, '')
    const match = cleaned.match(/(\d+)\/(\d+)/)
    if (match) {
      pointsEarned = match[1]
      totalPoints = match[2]
      const denom = parseInt(match[2])
      if (denom > 0) {
        scorePercentage = Math.round((parseInt(match[1]) / denom) * 100) + '%'
      }
      return true
    }
    return false
  }

  // 1. Old gauntlet/roleplay encounters from section_data
  if (sectionData.battles_won !== undefined && sectionData.battles_completed !== undefined) {
    pointsEarned = String(sectionData.battles_won)
    totalPoints = String(sectionData.battles_completed)
    const completed = parseInt(sectionData.battles_completed)
    if (completed > 0) {
      scorePercentage = Math.round((parseInt(sectionData.battles_won) / completed) * 100) + '%'
    }
    return { pointsEarned, totalPoints, scorePercentage }
  }
  if (context.gauntlet_completed !== undefined && pointsEarned === '') {
    totalPoints = String(context.gauntlet_completed)
    return { pointsEarned, totalPoints, scorePercentage }
  }

  // 2. Numeric quiz_score or pitch_score at top level (no denominator)
  if (typeof context.quiz_score === 'number') {
    scorePercentage = String(context.quiz_score)
    return { pointsEarned, totalPoints, scorePercentage }
  }
  if (typeof context.pitch_score === 'number') {
    scorePercentage = String(context.pitch_score)
    return { pointsEarned, totalPoints, scorePercentage }
  }

  // 3. lesson_result object (only on completion/result event types)
  const lr = context.lesson_result
  if (lr && typeof lr === 'object') {
    const isCompletionEvent = COMPLETION_EVENT_TYPES.includes(eventType) ||
      (eventType === 'Lesson Section Completed' && !context.section_data && !context.section_type)
    const isZeroed = (lr.correct_answers === 0 || lr.correct_answers === '0') &&
      (lr.total_questions === 0 || lr.total_questions === '0')

    if (isCompletionEvent && !isZeroed) {
      const correct = parseInt(lr.correct_answers)
      const total = parseInt(lr.total_questions)
      if (!isNaN(correct) && !isNaN(total) && total > 0) {
        pointsEarned = String(correct)
        totalPoints = String(total)
        scorePercentage = Math.round((correct / total) * 100) + '%'
        return { pointsEarned, totalPoints, scorePercentage }
      }
    }
  }

  // 4. Quiz score with extra text in parentheses
  if (typeof context.quiz_score === 'string' && parseFraction(context.quiz_score)) {
    return { pointsEarned, totalPoints, scorePercentage }
  }
  if (typeof sectionData.quiz_score === 'string' && parseFraction(sectionData.quiz_score)) {
    return { pointsEarned, totalPoints, scorePercentage }
  }

  // 5. Numeric scores in section_data
  if (typeof sectionData.quiz_score === 'number') {
    scorePercentage = String(sectionData.quiz_score)
    return { pointsEarned, totalPoints, scorePercentage }
  }
  if (typeof sectionData.pitch_score === 'number') {
    scorePercentage = String(sectionData.pitch_score)
    return { pointsEarned, totalPoints, scorePercentage }
  }

  // 6. Summary text regex as last resort
  if (log.summary) {
    const summaryMatch = log.summary.match(/Score:\s*(\d+)\/(\d+)/i) || log.summary.match(/(\d+)\/(\d+)/)
    if (summaryMatch && parseFraction(summaryMatch[0])) {
      return { pointsEarned, totalPoints, scorePercentage }
    }
  }

  return { pointsEarned, totalPoints, scorePercentage }
}

export function extractDetails(context) {
  const sd = context.section_data || {}
  const sType = context.section_type

  const row1 = []
  if (context.persona) row1.push({ label: 'Persona', value: context.persona })
  if (context.disposition) row1.push({ label: 'Disposition', value: context.disposition })
  if (context.role) row1.push({ label: 'Role', value: context.role })
  if (context.role_level) row1.push({ label: 'Role Level', value: context.role_level })

  const row2 = []
  if (context.mode) row2.push({ label: 'Mode', value: context.mode })
  if (context.focus_area) row2.push({ label: 'Focus Area', value: context.focus_area })
  if (context.lesson_key) row2.push({ label: 'Lesson Key', value: context.lesson_key })
  if (sType === 'objectives') {
    const engagement = context.training_engagement ?? sd.training_engagement
    if (engagement) row2.push({ label: 'Engagement', value: String(engagement) })
    if (context.skipped === true) row2.push({ label: 'Skipped', value: 'Yes' })
    if (context.objectives_covered !== undefined)
      row2.push({ label: 'Objectives Covered', value: String(context.objectives_covered) })
  }

  return { row1, row2 }
}

export function formatContextLegacy(log) {
  const context = log.context || {}
  const eventType = log.event_type || ''
  const lr = context.lesson_result
  const sectionData = context.section_data || {}

  const getTitle = () =>
    context.module_name || context.current_lesson || context.lesson_type || 'Lesson'

  // 1. Completion events with lesson_result
  if (COMPLETION_EVENT_TYPES.includes(eventType) && lr && typeof lr === 'object') {
    const isZeroed = (lr.correct_answers === 0 || lr.correct_answers === '0') &&
      (lr.total_questions === 0 || lr.total_questions === '0')
    const correct = parseInt(lr.correct_answers)
    const total = parseInt(lr.total_questions)
    const score = (!isZeroed && !isNaN(correct) && !isNaN(total) && total > 0)
      ? `${correct}/${total}`
      : null
    return {
      title: getTitle(),
      section: 'LESSON RESULT',
      score,
      passed: resolvePassedSafe(context, score),
      breakdown: [],
      details: extractDetails(context)
    }
  }

  // 2. Lesson Completed with numeric top-level scores (no lesson_result)
  if (COMPLETION_EVENT_TYPES.includes(eventType) && !lr) {
    const breakdown = []
    if (context.quiz_score !== undefined) breakdown.push({ name: 'Quiz Score', score: String(context.quiz_score) })
    if (context.pitch_score !== undefined) breakdown.push({ name: 'Pitch Score', score: String(context.pitch_score) })
    if (context.gauntlet_completed !== undefined) breakdown.push({ name: 'Gauntlet', score: String(context.gauntlet_completed) })
    if (context.gauntlet_performance) breakdown.push({ name: 'Performance', score: String(context.gauntlet_performance) })

    if (breakdown.length > 0) {
      return {
        title: getTitle(),
        section: 'LESSON SUMMARY',
        score: context.quiz_score !== undefined ? String(context.quiz_score) : null,
        passed: null,
        breakdown,
        details: extractDetails(context)
      }
    }
  }

  // 3. Lesson Section Completed with NO section_type AND NO section_data
  if (eventType === 'Lesson Section Completed' && !context.section_type && !context.section_data) {
    if (lr && typeof lr === 'object') {
      const isZeroed = (lr.correct_answers === 0 || lr.correct_answers === '0') &&
        (lr.total_questions === 0 || lr.total_questions === '0') &&
        (lr.score === 0 || lr.score === '0')
      if (isZeroed) return null

      const correct = parseInt(lr.correct_answers)
      const total = parseInt(lr.total_questions)
      const score = (!isNaN(correct) && !isNaN(total) && total > 0)
        ? `${correct}/${total}`
        : null

      const sectionLabel = context.lesson_type
        ? context.lesson_type.toUpperCase()
        : 'SECTION'

      return {
        title: context.module_name || context.lesson_type || 'Unknown Module',
        section: sectionLabel,
        score,
        passed: resolvePassedSafe(context, score),
        breakdown: [],
        details: extractDetails(context)
      }
    }
  }

  // 4. Gauntlet/roleplay section_data without section_type
  if (sectionData.battles_completed !== undefined) {
    const won = sectionData.battles_won
    const completed = sectionData.battles_completed
    const encounterDisplay = won !== undefined
      ? `${won}/${completed} encounters won`
      : `${completed} encounters`

    const breakdown = []
    const perfSummary = sectionData.performance_summary
    if (perfSummary) {
      String(perfSummary).split(/[;,]/).map(e => e.trim()).filter(Boolean).forEach(entry => {
        const parts = entry.split(':')
        if (parts.length === 2) {
          breakdown.push({ name: parts[0].trim(), score: parts[1].trim() })
        } else {
          breakdown.push({ name: entry, score: '' })
        }
      })
    }

    return {
      title: context.module_name || context.lesson_type || 'Unknown Module',
      section: context.section_type ? context.section_type.toUpperCase() : 'GAUNTLET',
      score: encounterDisplay,
      passed: null,
      breakdown,
      details: extractDetails(context)
    }
  }

  return null
}
